import { CollapsibleFilters } from "../../components/common/CollapsibleFilters";
import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Input,
  Stack,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/joy";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import "./dealProposals.css";
import { ResponsiveTable } from "../../components/common/ResponsiveTable";
import ProposalList from "./ProposalList";
import {
  proposalDealsApi,
  type DealType,
  type DealSummary,
  dealError,
} from "../../lib/proposalDealsApi";

function DealList({ type }: { type: DealType }) {
  const client = useQueryClient();
  const remove = useMutation({
    mutationFn: async (deal: DealSummary) => {
      const revision =
        deal.revision ?? (await proposalDealsApi.get(deal.id)).revision;
      return proposalDealsApi.remove(deal.id, revision);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["proposal-deals"] });
      setPage(1);
    },
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["proposal-deals", type, search, page],
    queryFn: () =>
      proposalDealsApi.list(
        new URLSearchParams({
          deal_type: type,
          search,
          page: String(page),
        }).toString(),
      ),
  });
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          useFlexGap
          flexWrap="wrap"
          gap={1}
        >
          <div>
            <Typography level="h3">
              {type === "insurance" ? "Insurance" : "Repair"} proposals
            </Typography>
            <Typography level="body-sm" sx={{ mt: 1 }}>
              {type === "insurance"
                ? "Inspections, claim assistance, and homeowner agreements."
                : "Defined scopes, clear prices, and authorized repairs."}
            </Typography>
          </div>
          <Button
            component={Link}
            to={`/crm/proposals/new?deal_type=${type}`}
            startDecorator={<Plus />}
            sx={{ alignSelf: "center" }}
          >
            Create proposal
          </Button>
        </Stack>
        <CollapsibleFilters activeCount={search.trim() ? 1 : 0}>
          <Input
            startDecorator={<MagnifyingGlass size={19} />}
            sx={{ maxWidth: 460 }}
            aria-label="Search proposals"
            placeholder="Search proposals…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </CollapsibleFilters>
        {query.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => void query.refetch()}>Retry</Button>
            }
          >
            Proposals could not be loaded.
          </Alert>
        )}
        {query.isPending && <Typography>Loading proposals…</Typography>}
        {query.data && !query.data.results.length && (
          <Card>
            <Typography level="title-md">
              {search
                ? "No matching proposals"
                : `Your ${type} proposals will appear here`}
            </Typography>
            <Typography level="body-sm">
              {search
                ? "Try a different customer, title, or proposal number."
                : "Start a proposal from an opportunity. Your team’s workflow defaults will be applied automatically."}
            </Typography>
            {search && (
              <Button
                variant="plain"
                onClick={() => setSearch("")}
                sx={{ alignSelf: "start" }}
              >
                Clear search
              </Button>
            )}
          </Card>
        )}
        {remove.isError && (
          <Alert color="danger">{dealError(remove.error)}</Alert>
        )}
        {remove.isSuccess && <Alert color="success">Proposal deleted.</Alert>}
        {!!query.data?.results.length && (
          <Box
            sx={{
              minWidth: 0,
              "& th, & td": { whiteSpace: "normal", overflowWrap: "anywhere" },
              "& .MuiChip-root": { maxWidth: "100%", whiteSpace: "normal" },
            }}
          >
            <ResponsiveTable
              rows={query.data.results}
              getRowKey={(d) => d.id}
              actionsWidth="90px"
              columns={[
                {
                  key: "proposal",
                  header: "Proposal",
                  mobileVariant: "header",
                  render: (d) => (
                    <>
                      <Link to={`/crm/proposals/deals/${d.id}`}>
                        {d.proposal_number}
                      </Link>
                      <Typography level="body-sm">{d.title}</Typography>
                    </>
                  ),
                },
                {
                  key: "customer",
                  header: "Customer / opportunity",
                  render: (d) => (
                    <>
                      {d.contact_name}
                      <Typography level="body-sm">
                        {d.opportunity_name}
                      </Typography>
                    </>
                  ),
                },
                {
                  key: "progress",
                  header: "Progress",
                  render: (d) => <Chip size="sm">{d.status_label}</Chip>,
                },
                {
                  key: "value",
                  header: "Value",
                  render: (d) =>
                    d.totals
                      ? Number(d.totals.total).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      : "Not priced",
                },
              ]}
              rowActions={(d) => (
                <Dropdown>
                  <MenuButton
                    size="sm"
                    disabled={remove.isPending}
                    aria-label={`Actions for ${d.proposal_number}`}
                  >
                    More
                  </MenuButton>
                  <Menu>
                    <MenuItem
                      component={Link}
                      to={`/crm/proposals/deals/${d.id}`}
                    >
                      Open proposal
                    </MenuItem>
                    <MenuItem
                      color="danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete ${d.proposal_number}? This removes the proposal and cancels unsigned agreements and shared report links. Signed or authorized proposals cannot be deleted.`,
                          )
                        )
                          remove.mutate(d);
                      }}
                    >
                      Delete
                    </MenuItem>
                  </Menu>
                </Dropdown>
              )}
            />
          </Box>
        )}
        {query.data && query.data.count > 20 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography>
              Page {query.data.page} · {query.data.count} proposals
            </Typography>
            <Button
              variant="outlined"
              disabled={page * 20 >= query.data.count}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
export default function ProposalHub() {
  const [params, setParams] = useSearchParams();
  const type = params.get("deal_type");
  return (
    <>
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2 }}>
        <nav
          className="deal-nav"
          aria-label="Proposal deal type"
          style={{ maxWidth: 410 }}
        >
          {(["retail", "insurance", "repair"] as const).map((t) => (
            <button
              key={t}
              aria-pressed={(type || "retail") === t}
              onClick={() => setParams(t === "retail" ? {} : { deal_type: t })}
            >
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
      </Box>
      {type === "insurance" || type === "repair" ? (
        <DealList key={type} type={type} />
      ) : (
        <ProposalList />
      )}
    </>
  );
}
